import type { Event } from 'models';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import {
  Button,
  Col,
  Flex,
  FormActions,
  GroupLineIcon,
  Image,
  Modal,
  QrCodeLineIcon,
  Row,
  Space,
  Tag,
  Text,
  Title,
} from 'ui';
import { images } from 'common/utils';
import { useGetEventTypeTagProperties } from '../../../../hooks';
import { useEventInviteesCount } from '../../../hooks';
import { SendInvitationsModal } from '../send-invitations-modal';
import { CopyInvitationLinkButton } from './copy-invitation-link-button';
import { ShareQrCodeModal } from './share-qr-code-modal';

type P = {
  event: Event;
  isOpened: boolean;
  onClose: () => void;
};

const InviteMorePeopleModal = ({ event, isOpened, onClose }: P) => {
  const { id, name, type } = event;
  const eventInviteesCount = useEventInviteesCount(event);
  const getEventTypeTagProperties = useGetEventTypeTagProperties();
  const [isSendInvitationsModalOpen, setIsSendInvitationsModalOpen] = useState(false);
  const [isShareQrCodeModalOpen, setIsShareQrCodeModalOpen] = useState(false);
  const [shareQrCodeModalType, setShareQrCodeModalType] = useState<'attendee' | 'presenter'>('attendee');

  return (
    <>
      <Modal
        isOpened={isOpened}
        maskProps={{
          closable: false,
        }}
        onCancel={() => {
          onClose();
        }}
        title="Invite more people"
      >
        <Modal.HugContent>
          <Space block orientation="vertical" size="large">
            <Row gutter={['ms', 0]}>
              <Col>
                <Image alt="Event cover photo" preview={false} src={images.default.eventCoverPhoto} width={128} />
              </Col>
              <Col>
                <Tag {...getEventTypeTagProperties({ eventType: type })} />
                <Title level={3}>{name}</Title>
              </Col>
            </Row>
            <Space block orientation="vertical" size="small">
              <Text type="secondary">How would you like to invite someone?</Text>
              <Button
                block
                icon={<GroupLineIcon />}
                onClick={() => {
                  setIsSendInvitationsModalOpen(true);
                }}
                size="large"
                type="secondary"
              >
                Send invitations
              </Button>
              <Flex gap="xs">
                <CopyInvitationLinkButton eventId={id} linkType="attendee" />
                <CopyInvitationLinkButton eventId={id} linkType="presenter" />
              </Flex>
              <Flex gap="xs">
                <Button
                  block
                  icon={<QrCodeLineIcon />}
                  onClick={() => {
                    setShareQrCodeModalType('attendee');
                    setIsShareQrCodeModalOpen(true);
                  }}
                  size="large"
                  type="secondary"
                >
                  <span>
                    <Trans components={{ strong: <strong /> }} i18nKey="QR code for attendee" />
                  </span>
                </Button>
                <Button
                  block
                  icon={<QrCodeLineIcon />}
                  onClick={() => {
                    setShareQrCodeModalType('presenter');
                    setIsShareQrCodeModalOpen(true);
                  }}
                  size="large"
                  type="secondary"
                >
                  <span>
                    <Trans components={{ strong: <strong /> }} i18nKey="QR code for presenter" />
                  </span>
                </Button>
              </Flex>
            </Space>
          </Space>
          <Modal.HugContent.Footer>
            <FormActions
              right={{
                children: 'Cancel',
                onClick() {
                  onClose();
                },
              }}
            />
          </Modal.HugContent.Footer>
        </Modal.HugContent>
      </Modal>
      <ShareQrCodeModal
        eventId={id}
        eventName={name}
        isOpened={isShareQrCodeModalOpen}
        onCancel={() => {
          setIsShareQrCodeModalOpen(false);
        }}
        type={shareQrCodeModalType}
      />
      <SendInvitationsModal
        eventId={id}
        eventInviteesCount={eventInviteesCount}
        isOpened={isSendInvitationsModalOpen}
        onClose={() => {
          setIsSendInvitationsModalOpen(false);
        }}
      />
    </>
  );
};

export default InviteMorePeopleModal;
